import { ApolloClient, ApolloLink, from, createHttpLink } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import FingerprintJS from '@fingerprintjs/fingerprintjs';
import { toast } from 'react-toastify';
import { cache, isLoggedVar } from './cache';
import { typeDefs } from './schema';
import { brandUrls } from '../../../lib/vars';

const httpLink = createHttpLink({
  uri: brandUrls().gq,
});

// busca el id del dispositivo (persistido en sessionStorage para que sobreviva al refresh)
const getDeviceId = async () => {
  const cached = sessionStorage.getItem('deviceId');
  if (cached) return cached;
  const fp = await FingerprintJS.load();
  const result = await fp.get();
  sessionStorage.setItem('deviceId', result.visitorId);
  return result.visitorId;
};

// Metadata del dispositivo para auditoría (resolución + idioma + versión real del OS + nombre)
// Se cachea porque no cambia durante la sesión (excepto deviceName que puede agregarse después del login)
let deviceMetaCache: string | null = null;
export const clearDeviceMetaCache = () => {
  deviceMetaCache = null;
};
const getDeviceMeta = async (): Promise<string> => {
  if (deviceMetaCache) return deviceMetaCache;
  const w = window.screen?.width || 0;
  const h = window.screen?.height || 0;
  const lang = navigator.language || '';

  // Detectar versión real del OS usando Client Hints (resuelve Windows 10 vs 11)
  let platformVersion = '';
  try {
    const uaData = (navigator as any).userAgentData;
    if (uaData?.getHighEntropyValues) {
      const hints = await uaData.getHighEntropyValues(['platformVersion', 'platform']);
      const major = parseInt(hints.platformVersion?.split('.')[0] || '0', 10);
      if (hints.platform === 'Windows' && major >= 13) {
        platformVersion = '11';
      } else if (hints.platform === 'Windows' && major > 0) {
        platformVersion = '10';
      }
    }
  } catch {
    // Client Hints no disponible, el backend usará ua-parser-js
  }

  const deviceName = localStorage.getItem('deviceName') || '';
  const meta = `${w}x${h}|${lang}|${platformVersion}|${deviceName}`;
  // Solo cachear si ya hay deviceName, para que al guardarlo post-login se incluya en el próximo request
  if (deviceName) deviceMetaCache = meta;
  return meta;
};

const authLink = setContext(async (_, { headers }) => {
  return {
    headers: {
      ...headers,
      authorization: localStorage.getItem('tokenAuth') || '',
      deviceid: (await getDeviceId()) || '',
      devicemeta: await getDeviceMeta(),
      client: 'ml.admin',
    },
  };
});

const errorHandler = onError(({ graphQLErrors }) => {
  if (graphQLErrors) {
    // eslint-disable-next-line no-unused-expressions
    graphQLErrors?.length &&
      graphQLErrors.forEach((err) => {
        const { code } = err.extensions;
        if (code === 'UNAUTHENTICATED' || code === 'FORBIDDEN')
          isLoggedVar({
            state: false,
            expired: true,
            message: 'Ha ocurrido un problema.',
            code: '500',
          });
        else if (code === 403) {
          localStorage.clear();
          isLoggedVar({
            state: false,
            expired: true,
            message: 'La sesión ha expirado.',
            code: code.toString(),
          });
        } else isLoggedVar({ ...isLoggedVar(), message: err.message, code: '' });
      });
  }
});

const miuvexResponseLink = new ApolloLink((operation, forward) =>
  forward(operation).map((response) => {
    const miuvex = response.extensions?.miuvex as
      | { error?: boolean; description?: string | null }
      | undefined;
    if (miuvex?.error && miuvex.description) {
      toast(miuvex.description, {
        autoClose: 10000,
        type: 'warning',
        hideProgressBar: true,
      });
    }
    return response;
  })
);

export const apolloClient = new ApolloClient({
  cache,
  link: from([errorHandler, miuvexResponseLink, authLink, httpLink]),
  typeDefs,
});
